import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useNotify } from "../../../context/NotificationContext";
import {
  Card,
  Form,
  Input,
  Select,
  Button,
  Typography,
  Space,
  Upload,
  InputNumber,
  Row,
  Col,
  Badge,
  Modal,
} from "antd";
import {
  SaveOutlined,
  ReloadOutlined,
  ArrowLeftOutlined,
  UploadOutlined,
  DeleteOutlined,
  PlusOutlined,
  VideoCameraOutlined,
  EyeOutlined,
  SortAscendingOutlined,
  PictureOutlined,
} from "@ant-design/icons";
import ImgCrop from "antd-img-crop";
import masterService from "../../../services/masterService";
import { noWhitespaceValidator } from "../../../utils/commonFunctions";
import { getImageUrl } from "../../../utils/commonFunctions";
import { useCommon } from "../../../context/CommonContext";

const { Title, Text } = Typography;
const { Option } = Select;
const GENRE_NAME_MAX_LENGTH = 255;
const GENRE_IMAGE_ASPECT = 320 / 180;

const getApiErrorMessage = (error, fallbackMessage) => {
  if (typeof error?.data?.message === "string" && error.data.message.trim()) {
    return error.data.message;
  }

  if (
    typeof error?.data?.message?.message === "string" &&
    error.data.message.message.trim()
  ) {
    return error.data.message.message;
  }

  return fallbackMessage;
};

const AddGenre = () => {
  const navigate = useNavigate();
  const { setPageTitle } = useCommon();
  const notify = useNotify();
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [languages, setLanguages] = useState([]);
  const [multilingualData, setMultilingualData] = useState([]);
  const [imageSets, setImageSets] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [previewTitle, setPreviewTitle] = useState("");
  const [cropAspect, setCropAspect] = useState(GENRE_IMAGE_ASPECT);

  useEffect(() => {
    setPageTitle("Add Genre");
    const loadLanguages = async () => {
      try {
        const params = {
          perPage: 1000,
          currentPage: 1,
          searchText: "",
          status: "Y",
        };
        const res = await masterService.getVideoLanguages(params);
        const list =
          res?.data?.data?.data || res?.data?.data || res?.data || [];
        setLanguages(list);
      } catch (e) {
        notify.error({ message: "Failed to load languages" });
      }
    };
    loadLanguages();
  }, [setPageTitle, notify]);

  const handleSubmit = async (values) => {
    const normalizedGenreName = String(values.genre_name || "").trim();
    if (normalizedGenreName.length > GENRE_NAME_MAX_LENGTH) {
      notify.error({
        message: `Genre name must be ${GENRE_NAME_MAX_LENGTH} characters or fewer`,
      });
      return;
    }

    // Validate multilingual data: no duplicate languages and require name if language selected
    const langs = multilingualData.map((d) => d.lang_id).filter(Boolean);
    if (new Set(langs).size !== langs.length) {
      notify.error({ message: "Duplicate languages in multilingual data" });
      return;
    }
    for (let i = 0; i < multilingualData.length; i++) {
      const d = multilingualData[i];
      const localizedGenreName = String(d.genre_name || "").trim();
      if (d.lang_id && !localizedGenreName) {
        notify.error({
          message: `Language entry #${i + 1
            }: Genre name is required when language is selected`,
        });
        return;
      }

      if (localizedGenreName.length > GENRE_NAME_MAX_LENGTH) {
        notify.error({
          message: `Language entry #${i + 1}: Genre name must be ${GENRE_NAME_MAX_LENGTH} characters or fewer`,
        });
        return;
      }
    }

    if (imageSets.length > 0) {
      const invalidImageSets = imageSets.some(
        (img) => !img || !img.lang_id || !img.card_image
      );
      if (invalidImageSets) {
        notify.error({
          message:
            "Please complete all image sets (language and genre image)",
        });
        return;
      }
      const imageLangs = imageSets.map((img) => img.lang_id).filter(Boolean);
      if (new Set(imageLangs).size !== imageLangs.length) {
        notify.error({ message: "Duplicate languages in image sets" });
        return;
      }
    }

    setLoading(true);
    try {
      // serialize parent genre_keywords (tags -> csv)
      const parentKeywords = Array.isArray(values.genre_keywords)
        ? values.genre_keywords
          .map((s) => String(s).trim())
          .filter(Boolean)
          .join(",")
        : values.genre_keywords || "";
      // serialize multilingual genre_keywords
      const multilingualSerialized = (multilingualData || []).map((d) => ({
        ...d,
        genre_name: String(d.genre_name || "").trim(),
        genre_keywords: Array.isArray(d.genre_keywords)
          ? d.genre_keywords
            .map((s) => String(s).trim())
            .filter(Boolean)
            .join(",")
          : d.genre_keywords || "",
      }));

      const normalizedImageSets = (imageSets || []).map((img) => ({
        lang_id: img?.lang_id || null,
        card_image: img?.card_image || "",
        movie_logo: img?.movie_logo || "",
      }));

      const payload = {
        ...values,
        genre_name: normalizedGenreName,
        genre_keywords: parentKeywords,
        multilingual_data: multilingualSerialized,
        images_arr: normalizedImageSets,
      };
      const response = await masterService.addGenre(payload);

      if (response.data && response.data.status) {
        notify.success({
          message: response.data.message || "Genre added successfully",
        });
        form.resetFields();
        navigate("/genres");
      } else {
        notify.error({
          message: response.data.message || "Failed to add genre",
        });
      }
    } catch (error) {
      console.error("Error adding genre:", error);
      notify.error({
        message: getApiErrorMessage(error, "Error adding genre"),
      });
    } finally {
      setLoading(false);
    }
  };

  const addImageSet = () => {
    setImageSets([
      ...imageSets,
      {
        lang_id: null,
        card_image: "",
        movie_logo: "",
      },
    ]);
  };

  const handleImageSetRemove = (index) => {
    setImageSets(imageSets.filter((_, i) => i !== index));
  };

  const handleImageRemove = (index, path) => {
    const updatedImageSets = [...imageSets];
    updatedImageSets[index][path] = "";
    setImageSets(updatedImageSets);
  };

  const handlePreview = (path, title) => {
    if (!path) {
      return;
    }
    setPreviewImage(getImageUrl(path));
    setPreviewTitle(title);
    setPreviewOpen(true);
  };

  const onImageUpload = async (file, keys) => {
    if (!file?.type?.startsWith("image/")) {
      notify.error({ message: "You can only upload image files" });
      return;
    }
    try {
      const formData = new FormData();
      formData.append("image_type", "images");
      formData.append("folder_name", "genre");
      formData.append("image", file);
      const response = await masterService.uploadImage(formData);
      const uploadedUrl = response.data.data.s3_key;
      const updatedImageSets = [...imageSets];
      updatedImageSets[keys.index][keys.path] = uploadedUrl;
      setImageSets(updatedImageSets);

      notify.success({ message: "Image uploaded successfully" });
    } catch (e) {
      notify.error({ message: "Failed to upload image" });
    }
  };

  const setAspectFromFile = (file) =>
    new Promise((resolve) => {
      if (!file?.type?.startsWith("image/")) {
        resolve(false);
        return;
      }

      const objectUrl = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        if (img.width && img.height) {
          setCropAspect(img.width / img.height);
        }
        URL.revokeObjectURL(objectUrl);
        resolve(true);
      };
      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        resolve(true);
      };
      img.src = objectUrl;
    });

  // Multilingual helpers
  const addMultilingualData = () => {
    setMultilingualData([
      ...multilingualData,
      {
        lang_id: null,
        genre_name: "",
        genre_description: "",
        genre_keywords: [],
      },
    ]);
  };
  const handleMultilingualDataRemove = (index) => {
    setMultilingualData(multilingualData.filter((_, i) => i !== index));
  };

  const handleReset = () => {
    setImageSets([]);
    form.resetFields();
  };

  const handleGoBack = () => {
    navigate("/genres");
  };

  return (
    <div className="min-h-screen">
      <div className="">
        {/* Header Section */}

        <Card>
          <Form
            form={form}
            layout="vertical"
            size="large"
            onFinish={handleSubmit}
            initialValues={{
              genre_is_active: "Y",
            }}
            className="space-y-6"
          >
            {/* Basic Information Section */}
            <div
              className="bg-white p-6 rounded-lg border border-gray-200"
              style={{ backgroundColor: "#ffffff", borderColor: "#d9d9d9" }}
            >
              <div className="flex items-center gap-2 mb-4">
                <VideoCameraOutlined />
                <Title
                  level={4}
                  className="!mb-0"
                  style={{ color: "rgba(0, 0, 0, 0.88)" }}
                >
                  Basic Information
                </Title>
              </div>

              <Row gutter={[24, 16]}>
                <Col xs={24} sm={12} lg={8}>
                  <Form.Item
                    label={
                      <span
                        className="font-semibold"
                        style={{ color: "rgba(0, 0, 0, 0.88)" }}
                      >
                        Genre Name
                      </span>
                    }
                    name="genre_name"
                    rules={[
                      { required: true, message: "Please enter genre name" },
                      { max: GENRE_NAME_MAX_LENGTH, message: `Genre name must be ${GENRE_NAME_MAX_LENGTH} characters or fewer` },
                      { validator: noWhitespaceValidator },
                    ]}
                  >
                    <Input
                      placeholder="Enter genre name"
                      maxLength={GENRE_NAME_MAX_LENGTH}
                      className="rounded-lg"
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12} lg={8}>
                  <Form.Item
                    label={
                      <span
                        className="font-semibold"
                        style={{ color: "rgba(0, 0, 0, 0.88)" }}
                      >
                        Status
                      </span>
                    }
                    name="genre_is_active"
                  >
                    <Select placeholder="Select status" className="rounded-lg">
                      <Option value="Y">
                        <Badge status="success" text="Active" />
                      </Option>
                      <Option value="N">
                        <Badge status="default" text="Inactive" />
                      </Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24}>
                  <Form.Item
                    label={
                      <span
                        className="font-semibold"
                        style={{ color: "rgba(0, 0, 0, 0.88)" }}
                      >
                        Description
                      </span>
                    }
                    name="genre_description"
                  >
                    <Input.TextArea
                      rows={4}
                      placeholder="Enter description"
                      className="rounded-lg"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24}>
                  <Form.Item
                    label={
                      <span
                        className="font-semibold"
                        style={{ color: "rgba(0, 0, 0, 0.88)" }}
                      >
                        Keywords
                      </span>
                    }
                    name="genre_keywords"
                  >
                    <Select
                      mode="tags"
                      placeholder="Type a keyword and press Enter"
                      tokenSeparators={[","]}
                      open={false}
                      className="rounded-lg"
                    />
                  </Form.Item>
                </Col>
              </Row>
            </div>

            {/* Preference Settings Section */}
            <div
              className="bg-white p-6 rounded-lg border border-gray-200"
              style={{ backgroundColor: "#ffffff", borderColor: "#d9d9d9" }}
            >
              <div className="flex items-center gap-2 mb-4">
                <EyeOutlined />
                <Title
                  level={4}
                  className="!mb-0"
                  style={{ color: "rgba(0, 0, 0, 0.88)" }}
                >
                  Preference Settings
                </Title>
              </div>

              <Row gutter={[24, 16]}>
                <Col xs={24} sm={12} lg={8}>
                  <Form.Item
                    label={
                      <span
                        className="font-semibold"
                        style={{ color: "rgba(0, 0, 0, 0.88)" }}
                      >
                        Show in Preference
                      </span>
                    }
                    name="show_in_preference"
                  >
                    <Select
                      placeholder="Select preference visibility"
                      className="rounded-lg"
                    >
                      <Option value="Y">
                        <Badge status="success" text="Yes" />
                      </Option>
                      <Option value="N">
                        <Badge status="default" text="No" />
                      </Option>
                    </Select>
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12} lg={8}>
                  <Form.Item
                    label={
                      <span
                        className="font-semibold"
                        style={{ color: "rgba(0, 0, 0, 0.88)" }}
                      >
                        Preference Order
                      </span>
                    }
                    name="preference_order_number"
                    rules={[
                      {
                        pattern: /^(0|[1-9]\d*)$/,
                        message:
                          "Preference order number must be a non-negative integer",
                      },
                    ]}
                  >
                    <InputNumber
                      placeholder="Enter preference order number"
                      style={{ width: "100%" }}
                      className="rounded-lg"
                      addonBefore={<SortAscendingOutlined />}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </div>

            {/* Media Assets Section */}
            <div
              className="bg-white p-6 rounded-lg border border-gray-200"
              style={{ backgroundColor: "#ffffff", borderColor: "#d9d9d9" }}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <PictureOutlined />
                  <Title
                    level={4}
                    className="!mb-0"
                    style={{ color: "rgba(0, 0, 0, 0.88)" }}
                  >
                    Image Sets
                  </Title>
                </div>
                <Button
                  type="dashed"
                  onClick={addImageSet}
                  icon={<PlusOutlined />}
                >
                  Add Image Set
                </Button>
              </div>

              {imageSets.length === 0 && (
                <Card style={{ textAlign: "center", background: "#fafafa" }}>
                  <Text type="secondary">No image sets added yet.</Text>
                </Card>
              )}

              <div className="space-y-4">
                {imageSets.map((image, index) => (
                  <Card
                    key={`genre-image-${index}`}
                    title={
                      <div className="flex items-center justify-between">
                        <Text strong>
                          Image Set #{index + 1}
                          {image.lang_id &&
                            ` - ${languages.find((l) => l.lang_id === image.lang_id)
                              ?.lang_name || "Unknown Language"
                            }`}
                        </Text>
                        <Button
                          type="link"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => handleImageSetRemove(index)}
                        >
                          Remove
                        </Button>
                      </div>
                    }
                  >
                    <Row gutter={[16, 16]}>
                      <Col xs={24}>
                        <Form.Item label="Language" required style={{ marginBottom: 0 }}>
                          <Select
                            value={image.lang_id}
                            placeholder="Select language"
                            onChange={(value) => {
                              const arr = [...imageSets];
                              arr[index].lang_id = value;
                              setImageSets(arr);
                            }}
                            showSearch
                            filterOption={(input, option) =>
                              option.children
                                .toLowerCase()
                                .indexOf(input.toLowerCase()) >= 0
                            }
                          >
                            {languages.map((lang) => (
                              <Option key={lang.lang_id} value={lang.lang_id}>
                                {lang.lang_name}
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item label="Genre Image">
                          {image.card_image && (
                            <div className="mb-4">
                              <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg mb-3">
                                <Text strong className="text-orange-700 text-sm">
                                  {getImageUrl(image.card_image)}
                                </Text>
                              </div>
                              <div className="w-full h-44 border border-gray-300 rounded-lg overflow-hidden flex items-center justify-center bg-gray-50">
                                <img
                                  src={getImageUrl(image.card_image)}
                                  alt="Card preview"
                                  className="w-full h-full object-contain"
                                />
                              </div>
                            </div>
                          )}
                          <ImgCrop
                            rotationSlider
                            zoomSlider
                            aspectSlider
                            showGrid
                            quality={1}
                            beforeCrop={setAspectFromFile}
                            cropperProps={{
                              initialAspectRatio: cropAspect,
                              autoCropArea: 0.9,
                              viewMode: 1,
                              background: false,
                              dragMode: "crop",
                              cropBoxResizable: true,
                              cropBoxMovable: true,
                              movable: true,
                              zoomable: true,
                            }}
                          >
                            <Upload
                              accept="image/*"
                              showUploadList={false}
                              beforeUpload={(f) => {
                                onImageUpload(f, { index, path: "card_image" });
                                return false;
                              }}
                            >
                              <Button
                                icon={<UploadOutlined />}
                                className="w-full rounded-lg"
                                type={image.card_image ? "default" : "primary"}
                              >
                                {image.card_image ? "Change Genre Image" : "Upload Genre Image"}
                              </Button>
                            </Upload>
                          </ImgCrop>
                          {image.card_image && (
                            <Space style={{ marginTop: 8 }}>
                              <Button
                                icon={<EyeOutlined />}
                                onClick={() =>
                                  handlePreview(
                                    image.card_image,
                                    `Genre Image #${index + 1}`
                                  )
                                }
                              >
                                View
                              </Button>
                              <Button
                                danger
                                icon={<DeleteOutlined />}
                                onClick={() =>
                                  handleImageRemove(index, "card_image")
                                }
                              >
                                Remove
                              </Button>
                            </Space>
                          )}
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item label="Genre Logo">
                          {image.movie_logo && (
                            <div className="mb-4">
                              <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg mb-3">
                                <Text strong className="text-orange-700 text-sm">
                                  {getImageUrl(image.movie_logo)}
                                </Text>
                              </div>
                              <div className="w-full h-44 border border-gray-300 rounded-lg overflow-hidden flex items-center justify-center bg-gray-50">
                                <img
                                  src={getImageUrl(image.movie_logo)}
                                  alt="Genre logo preview"
                                  className="max-w-full max-h-full object-contain"
                                />
                              </div>
                            </div>
                          )}
                          <ImgCrop
                            rotationSlider
                            zoomSlider
                            aspectSlider
                            showGrid
                            quality={1}
                            beforeCrop={setAspectFromFile}
                            cropperProps={{
                              initialAspectRatio: cropAspect,
                              autoCropArea: 0.9,
                              viewMode: 1,
                              background: false,
                              dragMode: "crop",
                              cropBoxResizable: true,
                              cropBoxMovable: true,
                              movable: true,
                              zoomable: true,
                            }}
                          >
                            <Upload
                              accept="image/*"
                              showUploadList={false}
                              beforeUpload={(f) => {
                                onImageUpload(f, { index, path: "movie_logo" });
                                return false;
                              }}
                            >
                              <Button
                                icon={<UploadOutlined />}
                                className="w-full rounded-lg"
                                type={image.movie_logo ? "default" : "primary"}
                              >
                                {image.movie_logo ? "Change Genre Logo" : "Upload Genre Logo"}
                              </Button>
                            </Upload>
                          </ImgCrop>
                          {image.movie_logo && (
                            <Space style={{ marginTop: 8 }}>
                              <Button
                                icon={<EyeOutlined />}
                                onClick={() =>
                                  handlePreview(
                                    image.movie_logo,
                                    `Genre Logo #${index + 1}`
                                  )
                                }
                              >
                                View
                              </Button>
                              <Button
                                danger
                                icon={<DeleteOutlined />}
                                onClick={() =>
                                  handleImageRemove(index, "movie_logo")
                                }
                              >
                                Remove
                              </Button>
                            </Space>
                          )}
                        </Form.Item>
                      </Col>
                    </Row>
                  </Card>
                ))}
              </div>
            </div>

            {/* Multilingual Data Section */}
            <div
              className="bg-white p-6 rounded-lg border border-gray-200"
              style={{ backgroundColor: "#ffffff", borderColor: "#d9d9d9" }}
            >
              <div className="flex items-center justify-between mb-4">
                <Title
                  level={4}
                  className="!mb-0"
                  style={{ color: "rgba(0, 0, 0, 0.88)" }}
                >
                  Multilingual Data
                </Title>
                <Button
                  type="dashed"
                  onClick={addMultilingualData}
                  icon={<UploadOutlined />}
                >
                  Add Language
                </Button>
              </div>

              {multilingualData.length === 0 && (
                <Card style={{ textAlign: "center", background: "#fafafa" }}>
                  <Text type="secondary">
                    No multilingual data added yet. Click "Add Language" to get
                    started.
                  </Text>
                </Card>
              )}

              <div className="space-y-4">
                {multilingualData.map((data, index) => (
                  <Card
                    key={`ml-${index}`}
                    title={
                      <div className="flex items-center justify-between">
                        <Text strong>
                          Language Entry #{index + 1}
                          {data.lang_id &&
                            ` - ${languages.find((l) => l.lang_id === data.lang_id)
                              ?.lang_name || "Unknown Language"
                            }`}
                        </Text>
                        <Button
                          type="link"
                          danger
                          onClick={() => handleMultilingualDataRemove(index)}
                        >
                          Remove
                        </Button>
                      </div>
                    }
                  >
                    <Row gutter={[16, 16]}>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          label="Language"
                          required
                          style={{ marginBottom: 0 }}
                        >
                          <Select
                            value={data.lang_id}
                            placeholder="Select language"
                            onChange={(value) => {
                              const arr = [...multilingualData];
                              arr[index].lang_id = value;
                              setMultilingualData(arr);
                            }}
                            showSearch
                            filterOption={(input, option) =>
                              option.children
                                .toLowerCase()
                                .indexOf(input.toLowerCase()) >= 0
                            }
                          >
                            {languages.map((lang) => (
                              <Option key={lang.lang_id} value={lang.lang_id}>
                                {lang.lang_name}
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          label="Genre Name"
                          required
                          style={{ marginBottom: 0 }}
                        >
                          <Input
                            value={data.genre_name}
                            placeholder="Enter genre name in this language"
                            maxLength={GENRE_NAME_MAX_LENGTH}
                            onChange={(e) => {
                              const arr = [...multilingualData];
                              arr[index].genre_name = e.target.value;
                              setMultilingualData(arr);
                            }}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row gutter={[16, 16]} style={{ marginTop: 12 }}>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          label="Description"
                          style={{ marginBottom: 0 }}
                        >
                          <Input.TextArea
                            rows={3}
                            value={data.genre_description}
                            placeholder="Enter description in this language"
                            onChange={(e) => {
                              const arr = [...multilingualData];
                              arr[index].genre_description = e.target.value;
                              setMultilingualData(arr);
                            }}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item label="Keywords" style={{ marginBottom: 0 }}>
                          <Select
                            mode="tags"
                            value={
                              Array.isArray(data.genre_keywords)
                                ? data.genre_keywords
                                : data.genre_keywords
                                  ? String(data.genre_keywords)
                                    .split(",")
                                    .map((s) => s.trim())
                                    .filter(Boolean)
                                  : []
                            }
                            placeholder="Type a keyword and press Enter"
                            onChange={(value) => {
                              const arr = [...multilingualData];
                              arr[index].genre_keywords = value;
                              setMultilingualData(arr);
                            }}
                            tokenSeparators={[","]}
                            open={false}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Card>
                ))}
              </div>
            </div>

            {/* Action Buttons */}
            <div
              className="flex justify-end gap-4 pt-6 border-t"
              style={{ borderColor: "#d9d9d9" }}
            >
              <Space size="middle">
                <Button
                  onClick={handleReset}
                  icon={<ReloadOutlined />}
                  size="large"
                  className="px-6"
                >
                  Reset
                </Button>
                <Button
                  onClick={handleGoBack}
                  icon={<ArrowLeftOutlined />}
                  size="large"
                  className="px-6"
                >
                  Cancel
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  icon={<SaveOutlined />}
                  size="large"
                  className="px-8"
                >
                  {loading ? "Adding..." : "Add Genre"}
                </Button>
              </Space>
            </div>
          </Form>
        </Card>
        <Modal
          open={previewOpen}
          title={previewTitle}
          footer={null}
          onCancel={() => setPreviewOpen(false)}
          width={700}
        >
          <img
            alt={previewTitle}
            src={previewImage}
            style={{ width: "100%", maxHeight: 500, objectFit: "contain" }}
          />
        </Modal>
      </div>
    </div>
  );
};

export default AddGenre;
